import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LaravelResourceResponse } from '../../../../../_base-shared/contracts/laravel-response.interface';
import { Creditor } from '../../../../../_base-shared/models/Entity/Creditor';
import { MainBaseApiService } from '../../_shared/services/main-base-api.service';
import {CaseCreditorProduct} from '../../../../../_base-shared/models/Product/CaseCreditorProduct';

@Injectable({
  providedIn: 'root',
})
export class CreditorService extends MainBaseApiService {

  public index(data): Observable<LaravelResourceResponse<Array<Creditor>>> {
    return this.http.get<LaravelResourceResponse<Array<Creditor>>>(`${ this.apiUrl }/creditors`, {params: data})
      .pipe(catchError(response => this.handleError(response)));
  }

  public index2(data): Observable<LaravelResourceResponse<Array<Creditor>>> {
    return this.http.get<LaravelResourceResponse<Array<Creditor>>>(`${ this.apiUrl }/public/creditors`, {params: data})
      .pipe(catchError(response => this.handleError(response)));
  }

  get(creditorId: number): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }`)
      .pipe(catchError(response => this.handleError(response)));
  }

  store(newCreditor: Creditor): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(`${ this.apiUrl }/creditors`, newCreditor)
      .pipe(catchError(response => this.handleError(response)));
  }

  update(creditorId: number, creditor: Creditor): Observable<LaravelResourceResponse> {
    return this.http.put<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }`, creditor)
      .pipe(
        catchError(response => this.handleError(response)),
        tap(response => {
          this.globalEvents.setUnapprovedCreditors(response.data.unapproved_creditors);
        }));
  }

  delete(creditorId: number): Observable<LaravelResourceResponse> {
    return this.http.delete<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }`)
      .pipe(catchError(response => this.handleError(response)));
  }

  deleteAndReassign(deleteId: number, reassignId: number, notes: string): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(`${ this.apiUrl }/creditors/reassign`,
      {reassign: reassignId, delete: deleteId, notes})
      .pipe(catchError(response => this.handleError(response)));
  }

  showReport(creditorId: number, data): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }/show-report`, {params: data})
      .pipe(catchError(response => this.handleError(response)));
  }

  showReportAll(): Observable<LaravelResourceResponse> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/show-full-report`)
      .pipe(catchError(response => this.handleError(response)));
  }

  downloadReport(creditorId: number): Observable<LaravelResourceResponse<any>> {
    return this.http.get<LaravelResourceResponse>(`${ this.apiUrl }/creditors/${ creditorId }/download-report`)
      .pipe(catchError(response => this.handleError(response)));
  }

  public verifySendMandates(caseId: number, data = null): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(`${ this.apiUrl }/cases/${caseId}/case-creditors/send-mandate`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendProposalAndAcoord(caseId: number, data: { selected_ids: Array<number> }): Observable<object> {
    return this.http.post(this.apiUrl + '/cases/' + caseId + '/case-creditors/send-proposal-and-accord', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  public sendRightOfAccess(caseId: number, data: { selected_ids: Array<number> }): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/cases/' + caseId + '/case-creditors/send-ppi', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  public sendExtrajudicialClaim(caseId: number, data: { selected_ids: Array<number> }): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/cases/' + caseId + '/case-creditors/send-extrajudicial-claim', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendRightOfAccessPpi(caseId: number, data: { selected_ids: Array<number> }): Observable<object> {
    return this.http.post(this.apiUrl + '/cases/' + caseId + '/case-creditors/send-ppi', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendRightOfAccessNotice(caseId: number, data: { selected_ids: Array<number>}): any {
    return this.http.post(this.apiUrl + '/cases/' + caseId + '/case-creditors/send-right-of-access-notice', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendPpiClaim(caseId: number, data: { selected_ids: Array<number> }): Observable<object> {
    return this.http.post(this.apiUrl + '/case-creditors/send-ppi-claim', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendAzcarateLoanLetter(caseId: number, data: { selected_ids: Array<number> }): Observable<object> {
    return this.http.post(this.apiUrl + '/cases/' + caseId + '/case-creditors/send-azcarate-loan', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendAzcarateRevolvingLetter(caseId: number, data: { selected_ids: Array<number> }): Observable<object> {
    return this.http.post(this.apiUrl + '/cases/' + caseId + '/case-creditors/send-azcarate-revolving', data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendAepEmails(caseId, data = null): Observable<object> {
    return this.http.post(`${ this.apiUrl }/cases/${ caseId }/creditors/send-aep`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  updateCaseCreditor(caseCreditorId, data): Observable<object> {
    return this.http.post(`${ this.apiUrl }/update-case-creditor/${ caseCreditorId }`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  updateCasePublicDebt(caseId, debtId, data): Observable<object> {
    return this.http.patch(`${ this.apiUrl }/cases/${ caseId }/public-debts/${ debtId }`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendLastChanceEmail(caseCreditorId, data = null): Observable<object> {
    return this.http.post(`${ this.apiUrl }/case-creditors/${ caseCreditorId }/send-last-chance`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendDeleteNumberEmail(caseCreditorId, data): Observable<object> {
    return this.http.post(`${ this.apiUrl }/case-creditors/${ caseCreditorId }/send-creditor-delete-phone`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendAntiHarassmentEmail(caseCreditorId, data = null): Observable<object> {
    return this.http.post(`${ this.apiUrl }/case-creditors/${ caseCreditorId }/send-azkarate-acoso`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendLoanCancellationEmail(caseCreditorId, data = null): Observable<object> {
    return this.http.post(`${ this.apiUrl }/case-creditors/${ caseCreditorId }/send-azkarate-cancelar`, data)
      .pipe(catchError(response => this.handleError(response)));
  }

  sendAzcarateEmail(caseCreditorId): Observable<object> {
    return this.http.post(`${ this.apiUrl }/case-creditors/${ caseCreditorId }/send-azcarate`, null)
      .pipe(catchError(response => this.handleError(response)));
  }

  getCaseCreditorProducts(): Observable<LaravelResourceResponse<Array<CaseCreditorProduct>>> {
    return this.http.get<LaravelResourceResponse<Array<CaseCreditorProduct>>>(`${ this.apiUrl }/creditors/products`)
      .pipe(catchError(response => this.handleError(response)));
  }

  public selectAsDDA(caseId: number, data: { selected_ids: Array<number> }): Observable<LaravelResourceResponse> {
    return this.http.post<LaravelResourceResponse>(this.apiUrl + '/cases/' + caseId + '/case-creditors/right-of-access-answered', data)
      .pipe(catchError(response => this.handleError(response)));
  }

}
